import { useState, useContext, useMemo } from "react";
import { Button } from "@/modern/components/ui/button";
import { RotateCcw, Loader2 } from "lucide-react";
import BryntumContext from "@/react/context/bryntumContext";
// @ts-expect-error - userPreferences is dynamically set in bryntumContext
import { userPreferences } from "@/react/context/bryntumContext.jsx";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { revertToFactoryDefaults, revertFieldAdvisorToDefault } from "@/network/network";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/modern/components/ui/dialog";
import { InspectionType, AdvisorType } from "../constants/settingsStructure";
import { getDraftKey, getDisplayName } from "../utils/promptUtils";
import { useBoardPermissions } from "../hooks/useBoardPermissions";
import { INSPECTION_PROMPT_OPTIONS, ADVISOR_PROMPT_OPTIONS } from "../detailComponents/AIPromptSelector";

interface RevertToFactoryButtonProps {
  inspectionType?: InspectionType;
  advisorType?: AdvisorType;
}

const RevertToFactoryButton = ({ inspectionType, advisorType }: RevertToFactoryButtonProps) => {
  const { project, selectedBoard } = useContext(BryntumContext);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isReverting, setIsReverting] = useState(false);

  const draftKey = getDraftKey(inspectionType, advisorType);
  const displayName = getDisplayName(inspectionType, advisorType);

  // @ts-expect-error - userPreferences is dynamically set in bryntumContext
  const hasOpenAIKey = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hasOpenAiApiKey, "false", true) === "true";

  // Use shared permission hook
  const { canWrite } = useBoardPermissions();
  const isDisabled = !canWrite || !hasOpenAIKey;

  // Get appropriate store based on type
  // @ts-expect-error - stores are added dynamically in projectConfig
  const store = advisorType ? project?.fieldAdvisorCardsStore : project?.contextCardsStore;

  // Get prompt options based on type (inspection vs advisor)
  const promptOptions = useMemo(() => {
    return advisorType ? ADVISOR_PROMPT_OPTIONS : INSPECTION_PROMPT_OPTIONS;
  }, [advisorType]);

  const handleRevert = async () => {
    if (!canWrite) {
      toast.error("You don't have permission to revert prompt cards");
      return;
    }

    // @ts-expect-error - selectedBoard.record type not fully defined
    if (!store || !project || !selectedBoard?.record?.id) {
      toast.error("Unable to revert: store not available");
      return;
    }

    setIsReverting(true);
    try {
      // Call appropriate backend endpoint to revert to factory defaults
      // @ts-expect-error - selectedBoard.record type not fully defined
      const boardId = selectedBoard.record.id;

      if (advisorType) {
        await revertFieldAdvisorToDefault(boardId, advisorType);
      } else {
        await revertToFactoryDefaults(boardId, inspectionType || "site_safety");
      }

      // Clear draft preference since we're back to defaults
      // @ts-expect-error - userPreferences is dynamically set in bryntumContext
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS[draftKey], "false", true);

      toast.success("Successfully reverted to factory defaults");
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error reverting to factory defaults:", error);
      toast.error("Failed to revert to factory defaults. Please try again.");
    } finally {
      setIsReverting(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setIsDialogOpen(true)}
        variant="outline"
        size="sm"
        className="shadow-sm"
        disabled={isDisabled || isReverting}
        title={!hasOpenAIKey ? "OpenAI API key required" : !canWrite ? "Board Owner permission required" : "Revert all AI prompt settings to factory defaults"}
      >
        {isReverting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
        Revert To Factory
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Revert to Factory Defaults?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 px-6 py-4">
            <p className="text-base text-muted-foreground">This will replace all current {displayName} prompt settings with factory defaults across all prompt sections:</p>
            <div className="bg-muted/50 rounded-lg p-4 border border-border">
              <ul className="grid grid-cols-2 gap-2">
                {promptOptions.map((option) => (
                  <li key={option.id} className="flex items-center gap-2 text-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                    <span className="text-foreground font-medium">{option.label}</span>
                    {option.required && (
                      <span className="text-xs text-primary font-medium">(Required)</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
              <p className="text-sm font-semibold text-destructive leading-relaxed">⚠️ All your custom changes will be lost. This action cannot be undone unless you publish before reverting.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isReverting} size="sm">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevert} disabled={isReverting} size="sm">
              {isReverting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reverting...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Revert to Factory
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RevertToFactoryButton;
